import { useQuery, UseQueryResult } from '@tanstack/react-query';

import { awaitJSON, StreamingOrdersLambdaFetch, fetchPresigned } from '../../utils/fetch-utils';

export interface UnmatchedDateRow {
  date: string;
  count: number;
}

export interface UnmatchedGroup {
  derivedNetwork: string;
  count: number;
  start: string;
  end: string;
  dates: UnmatchedDateRow[];
}

export interface SubFlightData {
  id: number;
  flight_id?: number;
  derived_network?: string;
  start?: string;
  end?: string;
  extended?: string;
  zero_length?: boolean;
}

export interface FlightData {
  id: number;
  order_id: number;
  derived_network?: string;
  start: string;
  end: string;
  extended?: string;
  spend: number;
  cpm: number;
  canceled?: boolean;
  subFlights?: SubFlightData[];
}

export interface NetworkData {
  network: string;
  parent?: string;
  description?: string;
  platform: string;
  dsp?: string;
  flights: FlightData[];
}

export interface WizardData {
  unmatchedGroups: UnmatchedGroup[];
  networkData: Record<string, NetworkData>;
}

export interface WizardDateRange {
  start: string;
  end: string;
}

export const useStreamingWizardData = (
  company: string,
  dates: WizardDateRange
): UseQueryResult<WizardData> =>
  useQuery({
    queryKey: ['streamingWizardData', company, dates.start, dates.end],
    enabled: Boolean(company),
    meta: { suppressGlobalError: true },
    queryFn: async (): Promise<WizardData> => {
      const res = await StreamingOrdersLambdaFetch('/wizard', {
        params: { company, ...dates }
      });
      const { url } = await awaitJSON<{ url: string }>(res);
      return awaitJSON<WizardData>(await fetchPresigned(url, 'Wizard data download failed'));
    }
  });

export type WizardActionPayload =
  | {
      derivedNetwork: string;
      type: 'ACCEPT';
      dates: string[];
      because: string;
    }
  | {
      derivedNetwork?: string;
      type: 'EXTEND';
      newExtended: string;
      id: number;
      isSubFlight: boolean;
    }
  | {
      derivedNetwork: string;
      type: 'ADD';
      startDate?: string;
      newExtended: string;
      id: number;
    };

// Imperative write — a plain async helper, not a query. Accept/Extend/Add all POST to the same
// endpoint with a `type` discriminator.
export const submitWizardAction = async (payload: WizardActionPayload): Promise<void> => {
  await StreamingOrdersLambdaFetch('/wizard', {
    method: 'POST',
    body: payload
  });
};
